import { supabase } from './supabase.js';
import { coerceUrl } from './url.js';

const SELECT_COLUMNS = [
  'id',
  'title',
  'category',
  'author',
  'year',
  'description',
  'url',
  'status',
  'submitted_by',
  'created_at',
  'updated_at',
].join(', ');

// Order matters: this is how the page lays the sections out.
export const ENGAGEMENT_CATEGORIES = ['theory_horizon', 'theory_implicit', 'practice'];

export const EMPTY_ENGAGEMENT = {
  title: '',
  category: 'theory_horizon',
  author: '',
  year: null,
  description: '',
  url: '',
};

export async function fetchPublishedEngagements() {
  const { data, error } = await supabase
    .from('broader_engagements')
    .select(SELECT_COLUMNS)
    .eq('status', 'published')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function fetchEngagement(id) {
  const { data, error } = await supabase
    .from('broader_engagements')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function normalize(form) {
  const blank = (v) => (v == null || v === '' ? null : v);
  return {
    title: form.title?.trim(),
    category: form.category,
    author: blank(form.author?.trim?.()),
    year: form.year === '' || form.year == null ? null : parseInt(form.year, 10),
    description: blank(form.description),
    url: coerceUrl(form.url),
  };
}

export function validateEngagement(form) {
  if (!form.title?.trim()) return 'broaderEngagements.form.errors.titleRequired';
  if (!ENGAGEMENT_CATEGORIES.includes(form.category)) {
    return 'broaderEngagements.form.errors.categoryRequired';
  }
  if (form.year !== null && form.year !== '' && form.year !== undefined) {
    const n = parseInt(form.year, 10);
    if (Number.isNaN(n) || n < 0 || n > 3000) return 'broaderEngagements.form.errors.yearInvalid';
  }
  return null;
}

// announce_in_whats_new is only written on create; see migration 019/021.
export async function createEngagement(form, { authorId, asAdmin }) {
  const row = {
    ...normalize(form),
    announce_in_whats_new: Boolean(form.announce_in_whats_new),
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  };
  const { data, error } = await supabase
    .from('broader_engagements')
    .insert(row)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateEngagement(id, form) {
  const { data, error } = await supabase
    .from('broader_engagements')
    .update(normalize(form))
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteEngagement(id) {
  const { error } = await supabase.from('broader_engagements').delete().eq('id', id);
  if (error) throw error;
}

// Group published rows by category, preserving the fetch order (newest first).
export function groupByCategory(items) {
  const groups = Object.fromEntries(ENGAGEMENT_CATEGORIES.map((c) => [c, []]));
  for (const item of items) {
    if (groups[item.category]) groups[item.category].push(item);
  }
  return groups;
}
